import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

const THRESHOLD = 80;
const REFRESHING_POSITION = 60;
const MAX_PULL = 150;
const RESISTANCE = 0.85;

const PTR_SPIN_CSS = '@keyframes ptr-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';

if (typeof document !== 'undefined') {
  const STYLE_ID = '__ptr_spin__';
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = PTR_SPIN_CSS;
    document.head.appendChild(s);
  }
}

export function PullToRefresh({ onRefresh, children, disabled = false }) {
  const containerRef = useRef(null);
  const startYRef = useRef(null);
  const isPullingRef = useRef(false);
  const pullDistRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  const [pullDist, setPullDist] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const reducedMotionRef = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const reducedMotion = reducedMotionRef.current;

  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || disabled) return;

    const onStart = (e) => {
      if (el.scrollTop > 0) { startYRef.current = null; return; }
      startYRef.current = e.touches[0].clientY;
      isPullingRef.current = false;
    };

    const onMove = (e) => {
      if (startYRef.current === null) return;
      if (refreshingRef.current) return;
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0 || el.scrollTop > 0) {
        setPullDist(0);
        pullDistRef.current = 0;
        isPullingRef.current = false;
        return;
      }
      isPullingRef.current = true;
      e.preventDefault();
      const dist = Math.min(MAX_PULL, Math.pow(delta, RESISTANCE));
      setPullDist(dist);
      pullDistRef.current = dist;
    };

    const onEnd = async () => {
      if (startYRef.current === null && !isPullingRef.current) return;
      startYRef.current = null;
      if (!isPullingRef.current) return;
      isPullingRef.current = false;
      const snap = pullDistRef.current;
      setPullDist(0);
      pullDistRef.current = 0;
      if (snap >= THRESHOLD && onRefreshRef.current && !disabled) {
        setRefreshing(true);
        refreshingRef.current = true;
        try { await onRefreshRef.current(); } catch (_) {}
        setRefreshing(false);
        refreshingRef.current = false;
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [disabled]);

  const isActive = pullDist > 0 || refreshing;
  const translateY = reducedMotion ? 0 : refreshing ? REFRESHING_POSITION : pullDist;
  const isSnapping = refreshing || (pullDist === 0 && !isPullingRef.current);

  return (
    <div
      data-testid="ptr-container"
      ref={containerRef}
      style={{
        position: 'relative',
        overflowY: 'auto',
        height: '100%',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {isActive && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 10,
            pointerEvents: 'none',
            opacity: refreshing ? 1 : Math.min(1, pullDist / 40),
            transform: `translateY(${reducedMotion ? 0 : refreshing ? REFRESHING_POSITION : pullDist}px)`,
            transition: isSnapping && !reducedMotion
              ? 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)'
              : 'none',
          }}
        >
          <div style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'var(--bg-L2)',
            border: '1px solid var(--border-default)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Loader2
              size={16}
              style={{
                color: 'var(--accent-online)',
                ...(refreshing
                  ? { animation: 'ptr-spin 0.6s linear infinite' }
                  : {
                      transform: `rotate(${Math.min(1, pullDist / THRESHOLD) * 360}deg)`,
                      transition: 'transform 0.1s ease',
                    }),
              }}
            />
          </div>
        </div>
      )}

      <div
        data-testid="ptr-content"
        style={{
          transform: `translateY(${translateY}px)`,
          transition: isSnapping && !reducedMotion
            ? 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)'
            : 'none',
          willChange: 'transform',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          height: '100%',
        }}
      >
        {children}
      </div>
    </div>
  );
}
